import type { SupportFollowupGroup, SupportFollowupItem } from "@/api/support";

/** Section order on the worklist page (matches the server's ORDER BY). */
export const FOLLOWUP_GROUP_ORDER: SupportFollowupGroup[] = ["payout_blocked", "trolley_returned", "individual"];

export const followupGroupTitle: Record<SupportFollowupGroup, string> = {
  payout_blocked: "출금 불가 (최우선)",
  trolley_returned: "Trolley 송금 반송",
  individual: "개별 대응",
};

export type FollowupFilter = "all" | "pending" | "done";

export const FOLLOWUP_FILTER_OPTIONS: Array<{ value: FollowupFilter; label: string }> = [
  { value: "all", label: "전체" },
  { value: "pending", label: "미완료" },
  { value: "done", label: "완료" },
];

export function isFollowupActionDone(item: SupportFollowupItem): boolean {
  return item.resolvedAt != null;
}

/** Done = internal action checked AND a staff reply actually sent to the customer. */
export function isFollowupDone(item: SupportFollowupItem): boolean {
  return isFollowupActionDone(item) && item.replied;
}

/** "문의 종결" is only offered once both checks are done and the ticket isn't already resolved. */
export function canCloseFollowup(item: SupportFollowupItem): boolean {
  return isFollowupDone(item) && item.ticket.status !== "resolved";
}

export interface FollowupProgress {
  total: number;
  resolved: number;
  replied: number;
  done: number;
}

export function computeFollowupProgress(items: SupportFollowupItem[]): FollowupProgress {
  return {
    total: items.length,
    resolved: items.filter(isFollowupActionDone).length,
    replied: items.filter((item) => item.replied).length,
    done: items.filter(isFollowupDone).length,
  };
}

export function formatFollowupProgress(progress: FollowupProgress): string {
  const { total, resolved, replied, done } = progress;
  return `조치 완료 ${resolved}/${total} · 회신 완료 ${replied}/${total} · 둘 다 완료 ${done}/${total}`;
}

export function filterFollowups(items: SupportFollowupItem[], filter: FollowupFilter): SupportFollowupItem[] {
  if (filter === "done") return items.filter(isFollowupDone);
  if (filter === "pending") return items.filter((item) => !isFollowupDone(item));
  return items;
}

export function groupFollowups(
  items: SupportFollowupItem[],
): Array<{ group: SupportFollowupGroup; title: string; items: SupportFollowupItem[] }> {
  return FOLLOWUP_GROUP_ORDER.map((group) => ({
    group,
    title: followupGroupTitle[group],
    items: items.filter((item) => item.group === group),
  })).filter((section) => section.items.length > 0);
}

export type BodySegment = { type: "text"; text: string } | { type: "quote"; text: string; lineCount: number };

/**
 * Splits a message body into plain text and e-mail quoted blocks (consecutive lines starting
 * with ">"). Only quote blocks of at least `minQuoteLines` lines are split out so the UI can
 * collapse them; joining every segment's `text` with "\n" reproduces the original body exactly.
 */
export function splitQuotedBlocks(body: string, minQuoteLines = 4): BodySegment[] {
  const lines = body.split("\n");
  const segments: BodySegment[] = [];
  let buffer: string[] = [];
  let bufferIsQuote = false;

  const flush = () => {
    if (buffer.length === 0) return;
    const text = buffer.join("\n");
    if (bufferIsQuote && buffer.length >= minQuoteLines) {
      segments.push({ type: "quote", text, lineCount: buffer.length });
    } else {
      const last = segments[segments.length - 1];
      if (last?.type === "text") last.text = `${last.text}\n${text}`;
      else segments.push({ type: "text", text });
    }
    buffer = [];
  };

  for (const line of lines) {
    const isQuote = /^\s*>/.test(line);
    if (buffer.length > 0 && isQuote !== bufferIsQuote) flush();
    bufferIsQuote = isQuote;
    buffer.push(line);
  }
  flush();
  return segments;
}
